<template>
  <div class="request-creator">
    <h4 class="mb-3 pb-3 border-bottom d-flex">
      {{ $t('ui.inquiry') }}

      <d-button
        v-if="!readOnly"
        theme="success"
        @click.prevent="addProduct"
        class="ml-auto">
        <i class="material-icons">add</i>
        {{ $t('product.addProduct') }}
      </d-button>
    </h4>

    <d-alert
      v-if="$route.params.product_id && !addedMoreProducts"
      show
      theme="success">
      {{ $t('inquiries.addMoreProductsInstructionInfo') }}
    </d-alert>

    <request-creator-product
      v-for="(productData, index) of productsData"
      :key="'product-' + index"
      :read-only="readOnly"
      :product-data="productData"
      :product-index="index"
      :currency-id="currencyID"
      :selected-currency-symbol="selectedCurrencySymbol"
      :products="products"
      :productsDiscount="productsDiscount"
      :isOrderView="isOrderView" />

    <d-alert
      v-if="!productsData.length && !readOnly"
      show="true"
      theme="light"
      class="text-center">
      {{ $t('inquiries.addFirstProductInstructionInfo') }}
    </d-alert>

    <d-row v-if="productsData.length">
      <h5
        v-if="totalOrderPrice.totalPrice"
        class="text-right mt-0 mb-3 ml-auto mr-3 w-100">
        <del
          v-if="totalOrderPrice.totalPrice !== totalOrderPrice.totalPriceBeforeDiscount"
          style="display: block; line-height: 1;">
          <small>
            {{ totalOrderPrice.totalPriceBeforeDiscount }}
            {{ selectedCurrencySymbol }}
          </small>
        </del>

        {{ $t('product.totalProductsCostNet') }}:
        <strong
          v-if="totalOrderPrice.totalPrice === totalOrderPrice.totalPriceBeforeDiscount"
          class="text-primary">
          {{ totalOrderPrice.totalPrice }}
          {{ selectedCurrencySymbol }}
        </strong>

        <strong
          v-if="totalOrderPrice.totalPrice !== totalOrderPrice.totalPriceBeforeDiscount"
          class="text-primary">
          {{ totalOrderPrice.totalPrice }}
          {{ selectedCurrencySymbol }}
        </strong>
      </h5>
    </d-row>

    <d-row
      v-if="productsData.length && !readOnly"
      class="px-3">
      <d-checkbox
        inline
        v-model="addServices"
        toggle
        toggle-small>
        {{ $t('service.wantToUseServices') }}
      </d-checkbox>
    </d-row>

    <d-row
      v-if="addServices"
      class="px-3 pt-4">
      <h4 class="mb-3 pb-3 border-bottom w-100">
        {{ $t('service.services') }}
      </h4>
      <p v-pure-html="$t('service.servicesSearchInstructionInfo')"></p>
    </d-row>

    <d-row
      v-if="addServices"
      class="pt-0">
      <d-col cols="8">
        <div :class="{ 'form-control': true, 'is-select': true }">
          <v-select
            :options="servicesGroups"
            v-model="servicesSearchID"
            :reduce="service => service.id"
            :filterBy="filterByForServicesGroups"
            :placeholder="$t('service.enterServiceCodeOrUseSearch')"
            label="code">
            <template slot="no-options">
              {{ $t('service.noServicesMatchingPhrase') }}&hellip;
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center">
                {{ option.code }} - {{ option.name }}
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center">
                {{ option.code }} - {{ option.name }}
              </div>
            </template>
          </v-select>
        </div>
      </d-col>

      <d-col cols="4">
        <d-button
          outline
          @click.prevent="$bus.$emit('request-creator-services-search-popup-open')">
          <i class="material-icons">search</i>
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          class="ml-2"
          :disabled="!servicesSearchID"
          @click.prevent="addServicesGroup()">
          {{ $t('inquiries.addServiceToInquiry') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="offerServicesGroups.length"
      class="px-3 pt-4">
      <h5 class="w-100">
        {{ $t('service.sericesAdded') }}:
      </h5>

      <d-card class="mb-4 w-100">
        <table class="table mb-0">
          <thead class="bg-light">
            <tr>
              <th scope="col" class="border-0 d-flex">
                <span> {{ $t('ui.name') }}</span>
              </th>
              <th scope="col" class="border-0" style="width: 160px;">{{ $t('service.servicePreview') }}</th>
              <th scope="col" class="border-0" style="width: 200px;">{{ $t('price.priceFrom') }}:</th>
              <th scope="col" class="border-0" style="width: 320px;">{{ $t('ui.additionalInfo') }}</th>
              <th scope="col" class="border-0" style="width: 80px;" v-if="!readOnly">{{ $t('ui.delete') }}</th>
            </tr>
          </thead>
          <tbody>
            <request-creator-services-group
              v-for="(servicesGroupData, index) of offerServicesGroups"
              :key="'services-group-' + index"
              :read-only="readOnly"
              :services-group-data="servicesGroupData"
              :services-group-index="index"
              :currency-id="currencyID"
              :selected-currency-symbol="selectedCurrencySymbol"
              :servicesGroups="servicesGroups"
              :services="services"
              :isOrderView="isOrderView" />
          </tbody>
        </table>
      </d-card>
    </d-row>

    <d-row
      v-if="readOnly && submittedFiles.length"
      class="px-3 pt-4">
      <h4 class="mb-3 pb-3 border-bottom w-100 d-flex">
        {{ $t('attachment.attachemnts') }}
      </h4>
    </d-row>

    <template v-if="submittedFiles">
      <d-row
        v-for="(file, index) of submittedFiles"
        :key="'input-file-' + index"
        class="px-3 pt-0 pb-3 flex-nowrap">
        <a
          href="#"
          @click.prevent="downloadFile(file.file_url, file.file)">
          <i class="material-icons">cloud_download</i>
          {{ file.file }}
        </a>
      </d-row>
    </template>

    <products-search-popup
      v-if="!readOnly && productsSearchPopupIsOpen"
      :currency-id="currencyID"
      :products-discount="productsDiscount"
      :selected-currency-symbol="selectedCurrencySymbol"
      :product-index="productsSearchPopupProductIndex"
      :tag-categories="tagCategories"
      :save-filters="true" />

    <request-creator-services-search-popup
      v-if="!readOnly && servicesSearchPopupIsOpen"
      :currency-id="currencyID"
      :selected-currency-symbol="selectedCurrencySymbol"
      :services="services" />

    <request-creator-service-prices-popup
      v-if="servicePricesPopupIsOpen"
      :currency-id="currencyID"
      :selected-currency-symbol="selectedCurrencySymbol"
      :service-group-id="selectedServiceGroupId"
      :services="services"
      :servicesGroups="servicesGroups" />
  </div>
</template>

<script>
import ProductsSearchPopup from './ProductsSearchPopup.vue';
import RequestCreatorProduct from './RequestCreatorProduct.vue';
import RequestCreatorServicesGroup from './RequestCreatorServicesGroup.vue';
import RequestCreatorServicesSearchPopup from './RequestCreatorServicesSearchPopup.vue';
import RequestCreatorServicePricesPopup from './RequestCreatorServicePricesPopup.vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import Vue from 'vue';

export default {
  name: 'request-creator',
  props: {
    readOnly: {
      default: false,
      type: Boolean
    },
    currencies: {
      default: () => ([]),
      type: Array
    },
    currencyID: {
      default: 0,
      type: Number
    },
    productsDiscount: {
      default: 0,
      type: Number
    },
    products: {
      default: () => ([]),
      type: Array
    },
    services: {
      default: () => ([]),
      type: Array
    },
    servicesGroups: {
      default: () => ([]),
      type: Array
    },
    tagCategories: {
      default: () => ([]),
      type: Array
    },
    isOrderView: {
      default: false,
      type: Boolean
    },
    submittedFiles: {
      default: () => ([]),
      type: Array
    }
  },
  components: {
    'products-search-popup': ProductsSearchPopup,
    'request-creator-product': RequestCreatorProduct,
    'request-creator-services-group': RequestCreatorServicesGroup,
    'request-creator-services-search-popup': RequestCreatorServicesSearchPopup,
    'request-creator-service-prices-popup': RequestCreatorServicePricesPopup
  },
  computed: {
    productTotalPrices () {
      return this.productsData.map(product => ([product.price, product.quantity].join(';'))).join('|');
    },
    totalOrderPrice () {
      let totalPrice = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalPrice)))
        .map(product => parseFloat(product.totalPrice))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalPriceBeforeDiscount = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalPriceBeforeDiscount)))
        .map(product => parseFloat(product.totalPriceBeforeDiscount))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      if (!totalPrice || Number.isNaN(totalPrice)) {
        return '';
      }

      return {
        totalPrice: CustomDecimals.roundProperly(totalPrice),
        totalPriceBeforeDiscount: CustomDecimals.roundProperly(totalPriceBeforeDiscount)
      };
    },
    selectedCurrencySymbol () {
      let foundedCurrency = this.currencies.filter(currency => currency.id === this.currencyID);

      if (foundedCurrency.length) {
        return foundedCurrency[0].code;
      }

      return '';
    }
  },
  watch: {
    productsData (newValue) {
      if (newValue.length === 0) {
        this.addServices = false;
        this.offerServicesGroups = [];
      }
    },
    productTotalPrices () {
      for (let i = 0; i < this.productsData.length; i++) {
        if (!Number.isNaN(parseFloat(this.productsData[i].price))) {
          this.productsData[i].totalPrice = CustomDecimals.roundProperly(this.productsData[i].quantity * parseFloat(this.productsData[i].price));
          this.productsData[i].totalPriceBeforeDiscount = CustomDecimals.roundProperly(this.productsData[i].quantity * parseFloat(this.productsData[i].price_before_discount));
        }
      }
    },
    addServices (newValue, oldValue) {
      if (newValue !== oldValue && newValue === false) {
        this.offerServicesGroups = [];
      }
    }
  },
  data () {
    return {
      addServices: false,
      addedMoreProducts: false,
      files: [],
      productsData: [],
      productsSearchPopupIsOpen: false,
      offerServicesGroups: [],
      servicesSearchID: null,
      servicesSearchPopupIsOpen: false,
      servicePricesPopupIsOpen: false,
      selectedServiceGroupId: 0
    };
  },
  mounted () {
    this.$bus.$on('products-search-popup-select-product', this.setProduct);
    this.$bus.$on('request-creator-remove-product', this.removeProduct);
    this.$bus.$on('request-creator-add-services-group', this.addServicesGroup);
    this.$bus.$on('request-creator-remove-services-group', this.removeServicesGroup);
    this.$bus.$on('products-search-popup-open', this.openProductsSearchPopup);
    this.$bus.$on('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$on('request-creator-services-search-popup-open', this.openServicesSearchPopup);
    this.$bus.$on('request-creator-services-search-popup-close', this.closeServicesSearchPopup);
    this.$bus.$on('request-creator-service-prices-popup-open', this.openServicePricesPopup);
    this.$bus.$on('request-creator-service-prices-popup-close', this.closeServicePricesPopup);

    if (this.$route.params.product_id) {
      this.addProduct();
      Vue.nextTick(() => {
        this.setProduct(parseInt(this.$route.params.product_id, 10), 0);
      });
    }
  },
  methods: {
    addProduct () {
      if (this.productsData.length >= 1) {
        this.addedMoreProducts = true;
      }

      this.productsData.push({
        product_id: null,
        productData: {},
        quantity: 1,
        price: '',
        totalPrice: '',
        customer_description: '',
        include_discounts: true,
        hasDescription: false,
        productsSearchPopupProductIndex: 0
      });
    },
    setProduct (productID, productIndex) {
      Vue.set(this.productsData[productIndex], 'product_id', productID);
    },
    addServicesGroup (servicesGroupID = null) {
      if (!this.servicesSearchID && !servicesGroupID) {
        return;
      }

      let id = this.servicesSearchID;

      if (servicesGroupID) {
        id = servicesGroupID;
      }

      this.offerServicesGroups.push({
        service_group_id: id,
        customer_description: ''
      });

      this.servicesSearchID = null;
    },
    removeProduct (index) {
      this.productsData.splice(index, 1);
    },
    removeServicesGroup (index) {
      this.offerServicesGroups.splice(index, 1);
    },
    prepareProductsData () {
      return this.productsData.map(product => ({
        product_id: product.product_id,
        price: CustomDecimals.roundProperly(parseFloat(product.price)),
        price_before_discount: CustomDecimals.roundProperly(parseFloat(product.price_before_discount)),
        quantity: product.quantity,
        include_discounts: product.include_discounts,
        customer_description: product.customer_description
      }));
    },
    prepareServicesGroupsData () {
      return this.offerServicesGroups.map(serviceGroup => ({
        service_group_id: serviceGroup.service_group_id,
        customer_description: serviceGroup.customer_description
      }));
    },
    filterByForServicesGroups (option, label, search) {
      return (option.code + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    openProductsSearchPopup (productIndex) {
      this.productsSearchPopupIsOpen = true;
      this.productsSearchPopupProductIndex = productIndex;
    },
    closeProductsSearchPopup () {
      this.productsSearchPopupIsOpen = false;
    },
    openServicesSearchPopup () {
      this.servicesSearchPopupIsOpen = true;
    },
    closeServicesSearchPopup () {
      this.servicesSearchPopupIsOpen = false;
    },
    openServicePricesPopup (serviceGroupID) {
      this.selectedServiceGroupId = serviceGroupID;
      this.servicePricesPopupIsOpen = true;
    },
    closeServicePricesPopup () {
      this.servicePricesPopupIsOpen = false;
    },
    setUpProducts (productsData) {
      this.productsData = productsData;
    },
    setUpServices (servicesGroupsData) {
      this.offerServicesGroups = servicesGroupsData;
    },
    addFile () {
      this.files.push(null);
    },
    removeFile (index) {
      this.files.splice(index, 1);
    },
    setFile (event, index) {
      this.files[index] = event.target.files[0];
    },
    downloadFile(fileUrl, fileName) {
      this.$http.post(fileUrl, {}, {
        responseType: 'blob'
      }).then((response) => {
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch((err) => {
        console.log('Error', err);
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('products-search-popup-select-product', this.setProduct);
    this.$bus.$off('request-creator-remove-product', this.removeProduct);
    this.$bus.$off('request-creator-add-services-group', this.addServicesGroup);
    this.$bus.$off('request-creator-remove-services-group', this.removeServicesGroup);
    this.$bus.$off('products-search-popup-open', this.openProductsSearchPopup);
    this.$bus.$off('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$off('request-creator-services-search-popup-open', this.openServicesSearchPopup);
    this.$bus.$off('request-creator-services-search-popup-close', this.closeServicesSearchPopup);
    this.$bus.$off('request-creator-service-prices-popup-open', this.openServicePricesPopup);
    this.$bus.$off('request-creator-service-prices-popup-close', this.closeServicePricesPopup);
  }
}
</script>
